import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Col, DatePicker, Form, Row
} from 'antd';
import _ from 'lodash';
import ExtendedSelect from 'components/extendedSelect';
import {
  convertToSelect, getKey, getLabel, setLowerDateRange, setUpperDateRange
} from 'library/helpers';
import { canViewOptimiser } from 'library/optimiser';
import settings from 'settings';
import { FormWrapper } from 'components/Page';
import REDUCER_KEY from 'reducers/reducer.keys';
import { list } from 'actions';

const isOptimiserEnabled = () => canViewOptimiser();

const getCollectionLabel = item => (isOptimiserEnabled() && item.used_by_optimiser ? `** ${item.name}` : item.name);

const FormItem = Form.Item;

const mapStateToProps = state => ({
  collections: state.api.collections.data.results,
  tagValues: state.api.tagValues.data.results,
  tagValuesInFlight: state.api.tagValues.inFlight,
  collectionsInFlight: state.api.collections.inFlight
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadTagCollections(searchText) {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [{
      id: 'theme',
      value: ownProps.parent.theme
    }];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    return dispatch(list(REDUCER_KEY.collections.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  },
  loadTagValues(searchText, collectionId) {
    const tagId = collectionId || ownProps.form.getFieldValue('tag_collection').key;
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [{
      id: 'collection',
      value: tagId
    }];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    return dispatch(list(REDUCER_KEY.tagValues.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = (props) => {
  const { dataSource } = props;
  const {
    tag_collection, tag_value, start_date, expiry_date, tag
  } = dataSource;

  return {
    tag_collection: Form.createFormField({ value: convertToSelect(tag_collection || (tag && tag.collection ? tag.collection : undefined)) }),
    start_date: Form.createFormField({ value: start_date }),
    expiry_date: Form.createFormField({ value: expiry_date }),
    tag_value: Form.createFormField({ value: convertToSelect(tag_value || tag) })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin, dataSource, inputFormItemLayout, form, loadTagValues, loadTagCollections, collections, tagValues, collectionsInFlight, tagValuesInFlight
}) => {
  const { getFieldDecorator } = form;
  const {
    tag_value, tag_collection, tag, start_date, expiry_date
  } = dataSource;
  const collection = tag_collection || (tag
  && tag.collection
  && tag.collection.id
    ? {
      key: tag.collection.id,
      label: isOptimiserEnabled() && tag.collection.used_by_optimiser ? `** ${tag.collection.name}` : tag.collection.name
    }
    : undefined);
  const tagValue = tag_value || (tag && tag.id
    ? {
      key: tag.id,
      label: tag.name
    }
    : undefined);
  const readonly = !canAdmin;
  const handleCollectionChange = (value) => {
    form.setFieldsValue({ tag_value: undefined });
    if (value && value.key) {
      loadTagValues('', value.key);
    }
  };
  return (
    <Form>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Tag Collection">
            {getFieldDecorator('tag_collection', {
              initialValue: collection,
              rules: [
                {
                  required: true,
                  message: 'Please select a Tag Collection.'
                }
              ]
            })(
              <ExtendedSelect
                placeholder="Select Tag Collection"
                fetching={collectionsInFlight}
                dataSource={collections}
                optionKey={getKey}
                optionLabel={getCollectionLabel}
                onSearch={loadTagCollections}
                onFocus={loadTagCollections}
                onChange={handleCollectionChange}
                disabled={readonly}
                dropdownMatchSelectWidth={false}
              />
            )}
            <span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>{isOptimiserEnabled() ? '** = Used by Optimiser' : ''}</span>
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Start Date">
            {getFieldDecorator('start_date', {
              initialValue: start_date
            })(
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD-MM-YYYY HH:mm"
                disabledDate={startValue => setUpperDateRange(startValue, expiry_date)}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Tag Value">
            {getFieldDecorator('tag_value', {
              initialValue: tagValue,
              rules: [
                {
                  required: true,
                  message: 'Please select a Tag Value.'
                }
              ]
            })(
              <ExtendedSelect
                placeholder="Select Tag"
                fetching={tagValuesInFlight}
                dataSource={tagValues}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadTagValues}
                onFocus={loadTagValues}
                disabled={readonly || !(collection ? collection.key || collection.id : false)}
                dropdownMatchSelectWidth={false}
              />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Expiry Date">
            {getFieldDecorator('expiry_date', {
              initialValue: expiry_date
            })(
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD-MM-YYYY HH:mm"
                disabledDate={endValue => setLowerDateRange(endValue, start_date)}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  collectionsInFlight: PropTypes.bool,
  tagValuesInFlight: PropTypes.bool,
  checkboxFormItemLayout: PropTypes.object,
  collections: PropTypes.array,
  tagValues: PropTypes.array,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  loadTagValues: PropTypes.func,
  loadTagCollections: PropTypes.func,
  inputFormItemLayout: PropTypes.object,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func
};

FormInput.defaultProps = {
  canAdmin: false,
  collectionsInFlight: false,
  tagValuesInFlight: false,
  checkboxFormItemLayout: {},
  collections: [],
  tagValues: [],
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  loadTagValues() {
  },
  loadTagCollections() {
  },
  onFieldsChange() {
  },
  onValuesChange() {
  }
};

const ConnectedFormInput = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormInput);

const TagsFormInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(ConnectedFormInput));

export default TagsFormInput;
